import { Link as ReachLink } from '@reach/router'
import React, { use, useEffect, ForwardedRef } from 'react'
import { useMergeRefs } from '@chakra-ui/icons'
import { motion } from 'framer-motion'
import { omit } from 'lodash'
import { ChevronLeft, ChevronRight } from '~/icons'
import { useMsg } from '~/common/localizationUtils'
import { useHasChangedOrMounted } from '~/hooks/usePrevious'
import { useRovingTabIndex } from '~/hooks/useRovingTabIndex'
import { $thm } from '~/common/themeSx'
import { asLink } from '../links/Links'
import {
  forwardRef,
  Tab,
  TabProps,
  TabList,
  TabListProps,
  Tabs,
  TabsProps,
  ButtonProps,
  BoxProps,
  chakra
} from '../../chakra'

type TabScrollArrowButtonProps = {
  direction: 'left' | 'right'
  onClick: () => void
}

const ARROW_BTN_WIDTH = 32
// framer's motion(chakra.button) redefines some DOM handlers (onAnimationStart, onDrag*) with
// signatures incompatible with React's DOM types, so those are omitted from forwarded props below.
// motion.create() is the framer-motion v11 API; motion() is deprecated in v11.
const MotionButton = motion.create(chakra.button)
const TabScrollArrowButton = forwardRef<TabScrollArrowButtonProps, 'button'>(({ direction, ...props }, ref) => {
  const msg = useMsg()
  return (
    <MotionButton
      ref={ref}
      aria-label={direction === 'left' ? msg('common.ui.scroll_left') : msg('common.ui.scroll_right')}
      aria-hidden="true"
      tabIndex={-1}
      key={`${direction}-arrow`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: 'linear' }}
      sx={{
        zIndex: 2,
        position: 'absolute',
        top: 0,
        [direction]: 0,
        height: '100%',
        background: 'white',
        border: '1px solid var(--vndly-color-container-border)',
        width: `${ARROW_BTN_WIDTH}px`
      }}
      {...omit(props, ['onAnimationStart', 'onDragStart', 'onDrag', 'onDragEnd'])}>
      {direction === 'left' ? <ChevronLeft /> : <ChevronRight />}
    </MotionButton>
  )
})

const useScrollableTabList = (props: ScrollableTabListProps, ref: ForwardedRef<HTMLElement>) => {
  const innerRef = React.useRef<HTMLDivElement>(null)
  const tabRefs = React.useRef<(HTMLDivElement | null)[]>([])
  const [showLeftArrowBtn, setShowLeftArrowBtn] = React.useState(false)
  const [showRightArrowBtn, setShowRightArrowBtn] = React.useState(false)

  const BTN_BORDER_WIDTH = 2
  const OFFSET = 40
  const scrollInDirection = (direction: 'left' | 'right') => {
    if (!innerRef.current || !tabRefs.current.length) return

    const currentScrollLeft = innerRef.current.scrollLeft
    const clientWidth = innerRef.current.clientWidth - ARROW_BTN_WIDTH
    const VISIBLE_PORTION = 2

    if (direction === 'right') {
      // Find the first tab to the right that is a quarter or less visible and scroll it so that it's right edge is at the right edge of the container
      for (let i = 0; i < tabRefs.current.length; i++) {
        const tab = tabRefs.current[i]
        if (tab) {
          const isLastTab = i === tabRefs.current.length - 1
          const isLessThanAQuarterVisible =
            tab.offsetLeft + tab.offsetWidth / VISIBLE_PORTION > currentScrollLeft + clientWidth
          if (isLastTab || isLessThanAQuarterVisible) {
            const newScrollLeft = Math.ceil(tab.offsetLeft + tab.offsetWidth - clientWidth + BTN_BORDER_WIDTH + OFFSET)
            const lastTab = tabRefs.current[tabRefs.current.length - 1]
            // If more than a quarter of the last tab is visible, just scroll to the end, don't make the user click the arrow
            // again just to make it scroll a tiny bit more.
            const shouldWeJustScrollToTheEnd =
              lastTab && lastTab.offsetLeft + lastTab.offsetWidth / 4 <= newScrollLeft + clientWidth
            innerRef.current.scrollTo({
              left: shouldWeJustScrollToTheEnd ? innerRef.current.scrollWidth : newScrollLeft,
              behavior: 'smooth'
            })
            break
          }
        }
      }
    } else {
      for (let i = tabRefs.current.length - 1; i >= 0; i--) {
        const tab = tabRefs.current[i]
        if (tab) {
          const isFirstTab = i === 0
          const isLessThanAQuarterVisible = tab.offsetLeft + tab.offsetWidth / VISIBLE_PORTION < currentScrollLeft
          if (isFirstTab || isLessThanAQuarterVisible) {
            const newScrollLeft = Math.ceil(tab.offsetLeft - ARROW_BTN_WIDTH - BTN_BORDER_WIDTH - OFFSET)
            const firstTab = tabRefs.current[0]
            // If more than a quarter of the first tab is visible, just scroll to the start, don't make the user click the arrow
            // again just to make it scroll a tiny bit more.
            const shouldWeJustScrollToTheStart =
              firstTab && firstTab.offsetLeft + firstTab.offsetWidth / 4 >= newScrollLeft
            innerRef.current.scrollTo({
              left: shouldWeJustScrollToTheStart ? 0 : newScrollLeft,
              behavior: 'smooth'
            })
            break
          }
        }
      }
    }
  }

  const scrollToTabIndex = (index: number) => {
    if (!innerRef.current || !tabRefs.current.length) return
    const tab = tabRefs.current[index]
    if (tab) {
      innerRef.current.scrollTo?.({
        left: Math.ceil(tab.offsetLeft - ARROW_BTN_WIDTH - BTN_BORDER_WIDTH - OFFSET),
        behavior: 'smooth'
      })
    }
  }

  const leftArrowButton = showLeftArrowBtn ? (
    <TabScrollArrowButton direction="left" onClick={() => scrollInDirection('left')} />
  ) : null
  const rightArrowButton = showRightArrowBtn ? (
    <TabScrollArrowButton direction="right" onClick={() => scrollInDirection('right')} />
  ) : null

  const mergedRef = useMergeRefs(ref, innerRef)

  const validChildren = React.Children.toArray(props.children).filter(Boolean)
  if (useHasChangedOrMounted(validChildren.length)) {
    tabRefs.current = []
  }
  const children = validChildren.map((child, index) =>
    React.cloneElement(child as React.ReactElement<{ ref?: (el: HTMLDivElement) => void }>, {
      ref: (el: HTMLDivElement) => {
        tabRefs.current[index] = el
      }
    })
  )

  React.useEffect(() => {
    const handleResize = () => {
      if (innerRef.current) {
        const hasMoreContentRight =
          Math.ceil(innerRef.current.scrollLeft) < innerRef.current.scrollWidth - innerRef.current.clientWidth
        const hasMoreContentLeft = innerRef.current.scrollLeft > 0
        setShowLeftArrowBtn(hasMoreContentLeft)
        setShowRightArrowBtn(hasMoreContentRight)
      }
    }
    handleResize()

    window.addEventListener('resize', handleResize)
    innerRef.current?.addEventListener('scroll', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      innerRef.current?.removeEventListener('scroll', handleResize)
    }
  }, [validChildren.length])

  useEffect(() => {
    const onFocus = (index: number) => () => {
      scrollToTabIndex(index)
    }

    tabRefs.current.forEach((tab, index) => {
      tab?.addEventListener?.('focus', onFocus(index))
    })

    return () => {
      tabRefs.current.forEach((tab, index) => {
        tab?.removeEventListener?.('focus', onFocus(index))
      })
    }
  }, [validChildren.length])

  return {
    leftArrowButton,
    rightArrowButton,
    getTabListProps: (): TabListProps => ({
      ...omit(props, 'hideUnderline'),
      sx: {
        overflowX: 'auto',
        padding: '2px 2px 0',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none'
        },

        borderBottom: props.hideUnderline ? 'none' : '1px solid var(--cnvs-sys-color-border-default)',
        '[data-page-header] &': {
          // PageHeader already draws a bottom border in legacy, so hide the tab underline.
          // Rebrand header is borderless, so keep the underline.
          borderBottom: { $base: 'none', $canvas: '1px solid var(--cnvs-sys-color-border-default)' }
        },
        ...props.sx
      },
      ref: mergedRef,
      children
    })
  }
}

type ScrollableTabListProps = {
  hideUnderline?: boolean
} & TabListProps

export const ScrollableTabList = forwardRef<ScrollableTabListProps, 'div'>((props, ref) => {
  const { leftArrowButton, rightArrowButton, getTabListProps } = useScrollableTabList(props, ref)

  return (
    <>
      {leftArrowButton}
      <TabList data-testid="tab-list" {...getTabListProps()} />
      {rightArrowButton}
    </>
  )
})

/**
 * Nav Tabs
 *
 * Used to navigate easily between views with different url paths within a page. This is the wrapper container for Nav tabs.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-tabs--docs
 *
 * @agent-gotcha Header tabs that route. Each NavTab = own Next.js page. Default for tabs in PageHeader; use NavTabs > NavTabList > NavTab. NOT PageTabs.
 */
export const NavTabs = forwardRef(({ children, ...props }: TabsProps, ref) => {
  return (
    <Tabs
      ref={ref}
      sx={{
        ...$thm({
          position: 'relative',
          '[data-page-header] &': {
            mb: '-1px'
          },
          borderBottom: { $canvas: '1px solid var(--cnvs-sys-color-border-default)' }
        }),
        ...props.sx
      }}
      {...props}>
      {children}
    </Tabs>
  )
})

NavTabs.displayName = 'NavTabs'

/**
 * Nav Tab List
 *
 * This component will be the wrapper to form a Nav tab list. It will automatically add an overflow menu when there are more than
 * five tabs.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-tabs--docs
 */
export const NavTabList = forwardRef<ScrollableTabListProps, 'div'>((props, ref) => {
  return <ScrollableTabList data-nav-tab-list ref={ref} hideUnderline {...props} />
})

NavTabList.displayName = 'NavTabList'

type NavTabProps = {
  href: any
} & TabProps

/**
 * Nav Tab
 *
 * Individual nav tab component. NavTab elements need to be wrapped inside the NavTabs. This component is a tab link
 * supporting NextJS links.
 *
 * For Mixpanel tracking, use the composition pattern with `trackedButtonClick`:
 *
 * @example
 * import { trackedButtonClick } from '~/third_party/analytics'
 * <NavTab href="/overview" onClick={trackedButtonClick(undefined, 'nav_overview')}>Overview</NavTab>
 *
 * Based on Chakra's Tab component.
 *
 * TODO: Move ToggledNextLink to the DS
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-tabs--docs
 */
export const NavTab = forwardRef<NavTabProps, 'button'>(({ onClick, ...props }, ref) => {
  /* @ts-ignore */
  return (
    <Tab
      {...asLink(props.href)}
      ref={ref}
      px={4}
      py={3}
      whiteSpace="nowrap"
      variant="tabLink"
      onClick={onClick}
      {...props}
    />
  )
})

NavTab.displayName = 'NavTab'

/**
 * Nav Tab Reach
 *
 * Individual nav tab component. NavTabReach elements need to be wrapped inside the NavTabs. This component is a tab link
 * supporting reach router links.
 *
 * Based on Chakra's Tab component.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-tabs--docs
 *
 * @deprecated Use NavTab with Next.js routing. Reach router is being phased out.
 */
export const NavTabReach = forwardRef<NavTabProps, 'button'>(({ href, ...props }, ref) => (
  <Tab as={ReachLink} to={href} px={4} py={3} whiteSpace="nowrap" variant="tabLink" ref={ref} {...props} />
))

NavTabReach.displayName = 'NavTabReach'

/**
 * Page Tabs
 *
 * This component is the parent wrapper of a series of PageTab components. Page tabs would be used when
 * tab that don't need to change url paths and stay within the same context is needed.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-tabs--docs
 *
 * @agent-gotcha In-page panel swap, no URL change. For page-level tabs in a PageHeader, strongly prefer NavTabs:
 * each tab gets its own URL (linkable/bookmarkable) and its own Next.js page (lazy-loaded, so panels that are
 * never visited are never rendered). PageTabs in a PageHeader is only acceptable for legacy non-Next.js pages
 * that aren't worth migrating (e.g. Work Types / Expenses / Calendar Profile settings), or genuinely lightweight
 * page content. Otherwise PageTabs is for tabs local to a card/drawer/section.
 */
export const PageTabs = forwardRef(({ children, ...props }: TabsProps, ref) => (
  <Tabs ref={ref} position="relative" {...props}>
    {children}
  </Tabs>
))

PageTabs.displayName = 'PageTabs'

/**
 * Page Tabs List
 *
 * This component will be the wrapper to form a page tab list. It will automatically add an overflow menu when there are more than
 * five tabs.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-tabs--docs
 */
export const PageTabList = forwardRef<ScrollableTabListProps, 'div'>((props, ref) => (
  <NavTabList ref={ref} hideUnderline={false} {...props} />
))

PageTabList.displayName = 'PageTabList'

/**
 * Page Tab
 *
 * Individual page tab component. Used to set each page tab as a button.
 *
 * Based on Chakra's Tab component
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-tabs--docs
 */
export const PageTab = forwardRef(({ children, ...props }: TabProps, ref) => (
  /* @ts-ignore */
  <Tab ref={ref} px={4} py={3} whiteSpace="nowrap" {...props}>
    {children}
  </Tab>
))

PageTab.displayName = 'PageTab'

type FilterTabsContextValue = {
  selectedValue?: string
  tabbableValue?: string
  onSelect: (value: string) => void
  onFocusTab: (value: string) => void
}

const FilterTabsContext = React.createContext<FilterTabsContextValue | null>(null)

export type FilterTabsProps = {
  value?: string
  defaultValue?: string
  // Method shorthand syntax (rather than an arrow-function-typed property) intentionally keeps this
  // bivariant, matching Chakra's RadioGroupProps['onChange'] this component used to have -- several
  // consumers pass a narrower, tenant-specific string union type (e.g. `(value: MyFilter) => void`).
  onChange?(value: string): void
} & Omit<BoxProps, 'onChange' | 'value' | 'defaultValue'>

/**
 * Filter Tabs
 *
 * This component is the parent wrapper of a series of FilterTab components. Filter tabs would be used when
 * prioritizing the same group of data. Used up to four tabs in a group.
 *
 * For Mixpanel tracking, use the composition pattern with `trackedTabChange`:
 *
 * @example
 * import { trackedTabChange } from '~/third_party/analytics'
 * <FilterTabs onChange={trackedTabChange(setView, 'filter_status', { count: 10 })}>
 *   <FilterTab value="pending">Pending</FilterTab>
 *   <FilterTab value="approved">Approved</FilterTab>
 * </FilterTabs>
 *
 * Uses ARIA's radiogroup/radio roles (this is a mutually-exclusive filter choice, not a set of tab panels),
 * but with the Tabs pattern's "manual activation" keyboard behavior rather than the radiogroup pattern's
 * default "selection follows focus": arrow keys move focus only (roving tabindex), and a click or
 * Enter/Space commits the selection. Selecting a filter tab typically triggers a data refetch, so changing
 * the selection merely by arrowing past a tab caused unwanted refetches for keyboard users. See VNDLY-62682.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-filter-tabs--docs
 */
export const FilterTabs = forwardRef<FilterTabsProps, 'div'>(
  ({ value: valueProp, defaultValue, onChange, children, onKeyDown, onFocus, onBlur, ...props }, ref) => {
    const msg = useMsg()

    const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue)
    const isControlled = valueProp !== undefined
    const selectedValue = isControlled ? valueProp : uncontrolledValue

    // Checking child.type (not just React.isValidElement) so a FilterTab wrapped in some other element
    // -- a Fragment, a Tooltip, a future consumer's own wrapper -- can't silently corrupt the roving
    // tabindex with an undefined value; it's just excluded instead.
    const values = React.Children.toArray(children)
      .filter(
        (child): child is React.ReactElement<FilterTabProps> => React.isValidElement(child) && child.type === FilterTab
      )
      .map(child => child.props.value)

    const {
      containerRef,
      tabbableValue,
      handleKeyDown: onRovingKeyDown,
      handleContainerFocus,
      handleContainerBlur,
      onItemFocus
    } = useRovingTabIndex({ values, activeValue: selectedValue })
    const mergedRef = useMergeRefs(ref, containerRef)

    const onSelect = React.useCallback(
      (value: string) => {
        if (!isControlled) setUncontrolledValue(value)
        onChange?.(value)
      },
      [isControlled, onChange]
    )

    // FilterTab reads this via context, so a fresh object on every render would force every FilterTab
    // to re-render whenever FilterTabs does, regardless of whether anything it actually reads changed.
    const contextValue = React.useMemo(
      () => ({ selectedValue, tabbableValue, onSelect, onFocusTab: onItemFocus }),
      [selectedValue, tabbableValue, onSelect, onItemFocus]
    )

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event)
      onRovingKeyDown(event)
    }

    const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
      onFocus?.(event)
      handleContainerFocus()
    }

    const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
      onBlur?.(event)
      handleContainerBlur(event)
    }

    return (
      <FilterTabsContext value={contextValue}>
        <chakra.div
          ref={mergedRef}
          role="radiogroup"
          display="flex"
          gap={2}
          aria-label={msg('common.action_bar.filter_tabs.group')}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...props}>
          {children}
        </chakra.div>
      </FilterTabsContext>
    )
  }
)

FilterTabs.displayName = 'FilterTabs'

export type FilterTabProps = {
  value: string
} & Omit<ButtonProps, 'value' | 'onClick'>

/**
 * Filter Tab
 *
 * Use up to four filter tabs.  Typically, you will want to include an “All” option.
 *
 * If possible, it is highly recommended to include counts on each item, as these can replace result counts in a list view.
 *
 * FilterTab elements need to be wrapped inside the FilterTabs
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-filter-tabs--docs
 */
export const FilterTab = forwardRef<FilterTabProps, 'button'>(({ value, sx, ...props }, ref) => {
  const context = use(FilterTabsContext)
  if (!context) throw new Error('FilterTab must be rendered inside FilterTabs')
  const { selectedValue, tabbableValue, onSelect, onFocusTab } = context

  return (
    <chakra.button
      ref={ref}
      type="button"
      role="radio"
      data-filter-tab
      aria-checked={selectedValue === value}
      tabIndex={tabbableValue === value ? 0 : -1}
      onFocus={() => onFocusTab(value)}
      onClick={() => onSelect(value)}
      textStyle="bodySmallSemi"
      sx={{
        appearance: 'none',
        background: 'none',
        border: 'none',
        outline: 'none',
        // Just the font-family reset (native buttons don't inherit it like other elements do) -- the
        // full `font` shorthand would also reset fontSize/fontWeight/lineHeight, clobbering textStyle
        // above, since sx always takes precedence over textStyle.
        fontFamily: 'inherit',
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        px: 3,
        h: 8,
        cursor: 'pointer',
        borderRadius: 'base',
        color: 'var(--vndly-color-tab-filter-text)',
        transitionProperty: 'var(--chakra-transition-property-common)',
        transitionDuration: 'var(--chakra-transition-duration-normal)',
        _hover: {
          bgColor: 'var(--vndly-color-tab-filter-background-hover)'
        },
        _active: {
          bgColor: 'var(--vndly-color-tab-filter-background-hover)'
        },
        _checked: {
          bgColor: 'var(--vndly-color-tab-filter-background-checked)'
        },
        _focusVisible: {
          boxShadow: 'outline'
        },
        ...sx
      }}
      {...props}
    />
  )
})

FilterTab.displayName = 'FilterTab'
